import {
  type BuildMarker,
  BuildMarkerSchema,
  type Game,
  GameSchema,
  type GameDevStage,
  GameDevStageSchema,
  type Milestone,
  MilestoneSchema,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

type RawGameRow = {
  id: unknown;
  studio_id: unknown;
  name: unknown;
  slug: unknown;
  short_id: unknown;
  settings: unknown;
  target_platforms: unknown;
  theme_sensitivity: unknown;
  created_at: unknown;
  updated_at: unknown;
};

type RawBuildMarkerRow = {
  id: unknown;
  game_id: unknown;
  label: unknown;
  version: unknown;
  stage_id: unknown;
  archived_at: unknown;
  origin: unknown;
  created_by_user_id: unknown;
  notes: unknown;
  released_at: unknown;
  created_at: unknown;
  updated_at: unknown;
};

type RawGameDevStageRow = {
  id: unknown;
  game_id: unknown;
  label: unknown;
  colour: unknown;
  semantic_tier: unknown;
  order_index: unknown;
  created_at: unknown;
  updated_at: unknown;
};

type RawMilestoneRow = {
  id: unknown;
  game_id: unknown;
  name: unknown;
  description: unknown;
  research_focus: unknown;
  filter_spec: unknown;
  created_by_user_id: unknown;
  created_at: unknown;
  updated_at: unknown;
};

function mapGameRow(raw: RawGameRow): Record<string, unknown> {
  return {
    id: raw.id,
    studioId: raw.studio_id,
    name: raw.name,
    slug: raw.slug,
    shortId: raw.short_id,
    settings: raw.settings,
    targetPlatforms: raw.target_platforms ?? [],
    themeSensitivity: raw.theme_sensitivity,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export function mapBuildMarkerRow(raw: RawBuildMarkerRow): Record<string, unknown> {
  return {
    id: raw.id,
    gameId: raw.game_id,
    label: raw.label,
    version: raw.version,
    stageId: raw.stage_id,
    archivedAt: raw.archived_at,
    origin: raw.origin,
    createdByUserId: raw.created_by_user_id,
    notes: raw.notes,
    releasedAt: raw.released_at,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export function mapGameDevStageRow(raw: RawGameDevStageRow): Record<string, unknown> {
  return {
    id: raw.id,
    gameId: raw.game_id,
    label: raw.label,
    colour: raw.colour,
    semanticTier: raw.semantic_tier,
    orderIndex: raw.order_index,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

function mapMilestoneRow(raw: RawMilestoneRow): Record<string, unknown> {
  return {
    id: raw.id,
    gameId: raw.game_id,
    name: raw.name,
    description: raw.description,
    researchFocus: raw.research_focus,
    filterSpec: raw.filter_spec,
    createdByUserId: raw.created_by_user_id,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export async function fetchGamesForStudio(studioId: string): Promise<Game[]> {
  const { data, error } = await supabase
    .from('games')
    .select('*')
    .eq('studio_id', studioId)
    .order('created_at', { ascending: true });

  if (error) {
    console.warn('[shell-data] Failed to load games:', error.message);
    return [];
  }

  const games: Game[] = [];
  for (const raw of (data ?? []) as RawGameRow[]) {
    const parsed = GameSchema.safeParse(mapGameRow(raw));
    if (parsed.success) {
      games.push(parsed.data);
    } else {
      console.warn('[shell-data] Could not parse game row; skipping.', parsed.error.issues);
    }
  }
  return games;
}

export type FetchBuildMarkersOptions = {
  // Default false: the shell context only ever needs the active list.
  // The Builds management page passes true to surface archived markers
  // behind a toggle.
  includeArchived?: boolean;
};

export async function fetchBuildMarkersForGame(
  gameId: string,
  options: FetchBuildMarkersOptions = {},
): Promise<BuildMarker[]> {
  let query = supabase
    .from('build_markers')
    .select('*')
    .eq('game_id', gameId)
    .order('released_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });

  if (options.includeArchived !== true) {
    query = query.is('archived_at', null);
  }

  const { data, error } = await query;

  if (error) {
    console.warn('[shell-data] Failed to load build markers:', error.message);
    return [];
  }

  const markers: BuildMarker[] = [];
  for (const raw of (data ?? []) as RawBuildMarkerRow[]) {
    const parsed = BuildMarkerSchema.safeParse(mapBuildMarkerRow(raw));
    if (parsed.success) {
      markers.push(parsed.data);
    } else {
      console.warn('[shell-data] Could not parse build marker row; skipping.', parsed.error.issues);
    }
  }
  return markers;
}

export async function fetchDevStagesForGame(gameId: string): Promise<GameDevStage[]> {
  const { data, error } = await supabase
    .from('game_dev_stages')
    .select('*')
    .eq('game_id', gameId)
    .order('order_index', { ascending: true });

  if (error) {
    console.warn('[shell-data] Failed to load dev stages:', error.message);
    return [];
  }

  const stages: GameDevStage[] = [];
  for (const raw of (data ?? []) as RawGameDevStageRow[]) {
    const parsed = GameDevStageSchema.safeParse(mapGameDevStageRow(raw));
    if (parsed.success) {
      stages.push(parsed.data);
    } else {
      console.warn('[shell-data] Could not parse dev stage row; skipping.', parsed.error.issues);
    }
  }
  return stages;
}

export async function fetchMilestonesForGame(gameId: string): Promise<Milestone[]> {
  const { data, error } = await supabase
    .from('milestones')
    .select('*')
    .eq('game_id', gameId)
    .order('created_at', { ascending: false });

  if (error) {
    console.warn('[shell-data] Failed to load milestones:', error.message);
    return [];
  }

  const milestones: Milestone[] = [];
  for (const raw of (data ?? []) as RawMilestoneRow[]) {
    const parsed = MilestoneSchema.safeParse(mapMilestoneRow(raw));
    if (parsed.success) {
      milestones.push(parsed.data);
    } else {
      console.warn('[shell-data] Could not parse milestone row; skipping.', parsed.error.issues);
    }
  }
  return milestones;
}
